import api from '../../api/axiosConfig';

export type WebPushServerStatus = {
  serverConfigured: boolean;
  vapidPublicKey: string;
  subscribed: boolean;
  subscriptionCount: number;
};

export type WebPushBrowserStatus = {
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  subscribed: boolean;
};

export type WebPushTestPayload = {
  eventType: string;
  title: string;
  body: string;
  url: string;
  tag: string;
};

const SERVICE_WORKER_PATH = '/policyintel-web-push-sw.js';
const SERVICE_WORKER_SCOPE = '/policyintel-web-push/';
const NOTIFICATION_ICON = '/images/policy-intel.png';
const NOTIFICATION_BADGE = '/images/policy-intel.png';

let serviceWorkerRegistrationPromise: Promise<ServiceWorkerRegistration> | null = null;

export function webPushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window;
}

export async function getWebPushServerStatus(): Promise<WebPushServerStatus> {
  const response = await api.get('/web-push/status');
  const data = response?.data ?? {};
  return {
    serverConfigured: data.serverConfigured === true,
    vapidPublicKey: String(data.vapidPublicKey ?? '').trim(),
    subscribed: data.subscribed === true,
    subscriptionCount: Number(data.subscriptionCount ?? 0),
  };
}

export async function getWebPushBrowserStatus(): Promise<WebPushBrowserStatus> {
  if (!webPushSupported()) {
    return {
      supported: false,
      permission: 'unsupported',
      subscribed: false,
    };
  }

  const registration = await ensureServiceWorker();
  const subscription = await registration.pushManager.getSubscription();
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: Boolean(subscription),
  };
}

export async function enableWebPush(vapidPublicKey: string): Promise<PushSubscription> {
  if (!webPushSupported()) {
    throw new Error('이 브라우저는 웹푸시를 지원하지 않습니다.');
  }
  if (!vapidPublicKey) {
    throw new Error('웹푸시 서버 공개키가 준비되지 않았습니다.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permission === 'denied'
      ? '브라우저 알림 권한이 차단되어 있습니다.'
      : '웹푸시를 사용하려면 알림 권한이 필요합니다.');
  }

  const registration = await ensureServiceWorker();
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  await saveSubscription(subscription);
  return subscription;
}

export async function syncExistingWebPushSubscription(): Promise<boolean> {
  if (!webPushSupported() || Notification.permission !== 'granted') {
    return false;
  }

  const registration = await ensureServiceWorker();
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return false;
  }

  await saveSubscription(subscription);
  return true;
}

async function getExistingServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!webPushSupported()) {
    return null;
  }

  return (await navigator.serviceWorker.getRegistration(SERVICE_WORKER_SCOPE)) ?? null;
}

/**
 * Manual authenticated logout path.
 *
 * 1) Disable the current endpoint on the server while auth still exists.
 * 2) Always unsubscribe the browser locally even if the server request fails.
 *
 * The Service Worker itself and Notification permission are intentionally kept.
 */
export async function disableWebPush(): Promise<void> {
  const registration = await getExistingServiceWorkerRegistration();
  if (!registration) {
    return;
  }

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return;
  }

  let serverError: unknown = null;

  try {
    await api.post('/web-push/unsubscribe', {
      endpoint: subscription.endpoint,
      p256dh: '',
      auth: '',
    });
  } catch (error) {
    serverError = error;
  }

  try {
    await subscription.unsubscribe();
  } catch (localError) {
    if (serverError) {
      throw serverError;
    }
    throw localError;
  }

  if (serverError) {
    throw serverError;
  }
}

/**
 * Session-expired/local-auth-loss path.
 * The authenticated server endpoint may no longer be callable, so stop delivery
 * to this browser immediately by invalidating the local PushSubscription.
 */
export async function disableWebPushLocalOnly(): Promise<void> {
  const registration = await getExistingServiceWorkerRegistration();
  if (!registration) {
    return;
  }

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return;
  }

  await subscription.unsubscribe();
}

export async function getWebPushTestPayload(): Promise<WebPushTestPayload> {
  const response = await api.get('/web-push/test-payload');
  const data = response?.data ?? {};
  return {
    eventType: String(data.eventType ?? 'TEST'),
    title: String(data.title ?? '[테스트] PolicyIntel 실시간 상태 변경'),
    body: String(data.body ?? '시장·종목 상태 변경 알림 미리보기입니다.'),
    url: String(data.url ?? '/alerts'),
    tag: String(data.tag ?? 'policyintel-webpush-preview'),
  };
}

export async function previewWebPushTest(): Promise<WebPushTestPayload> {
  if (!webPushSupported()) {
    throw new Error('이 브라우저는 웹푸시 알림 미리보기를 지원하지 않습니다.');
  }

  const permission = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();

  if (permission !== 'granted') {
    throw new Error(permission === 'denied'
      ? '브라우저 알림 권한이 차단되어 있습니다.'
      : '알림 화면 미리보기를 사용하려면 브라우저 알림 권한이 필요합니다.');
  }

  const [registration, payload] = await Promise.all([
    ensureServiceWorker(),
    getWebPushTestPayload(),
  ]);

  await registration.showNotification(payload.title, {
    body: payload.body,
    icon: NOTIFICATION_ICON,
    badge: NOTIFICATION_BADGE,
    tag: payload.tag,
    data: {
      url: payload.url,
      eventType: payload.eventType,
    },
  });

  return payload;
}

export async function sendWebPushTest(): Promise<number> {
  const response = await api.post('/web-push/test');
  return Number(response?.data?.sent ?? 0);
}

async function saveSubscription(subscription: PushSubscription): Promise<void> {
  const p256dh = keyToBase64Url(subscription.getKey('p256dh'));
  const auth = keyToBase64Url(subscription.getKey('auth'));

  if (!subscription.endpoint || !p256dh || !auth) {
    throw new Error('브라우저 웹푸시 구독키를 확인할 수 없습니다.');
  }

  await api.post('/web-push/subscribe', {
    endpoint: subscription.endpoint,
    p256dh,
    auth,
  });
}

async function ensureServiceWorker(): Promise<ServiceWorkerRegistration> {
  if (!webPushSupported()) {
    throw new Error('이 브라우저는 Service Worker를 지원하지 않습니다.');
  }

  if (serviceWorkerRegistrationPromise) {
    return serviceWorkerRegistrationPromise;
  }

  serviceWorkerRegistrationPromise = (async () => {
    const registration = await navigator.serviceWorker.register(
      SERVICE_WORKER_PATH,
      {
        scope: SERVICE_WORKER_SCOPE,
        updateViaCache: 'none',
      },
    );

    try {
      await registration.update();
    } catch (error) {
      console.warn(
        '[PolicyIntel WebPush] service worker update check failed',
        error,
      );
    }

    return registration;
  })();

  try {
    return await serviceWorkerRegistrationPromise;
  } catch (error) {
    serviceWorkerRegistrationPromise = null;
    throw error;
  }
}

function keyToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
